import { createSelector } from '@reduxjs/toolkit';
import { alphaSort } from 'vet-bones/utils';

import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { MappingFilterItem } from 'src/containers/ProjectMapping/types';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectMappingSelector } from 'src/selectors/projectMapping';
import { projectMappingAttributesSelector } from 'src/selectors/projectMappingAttributes';
import { ReduxState } from 'src/store/types';
import {
  MappingFilterCheckState,
  MappingFilterType,
} from 'src/store/ui/constants';
import * as copy from 'src/templates/copy';

export const ALL_ATTRIBUTES_CHECKED_STATE = {
  [MappingFilterType.ALL_ITEMS]: MappingFilterCheckState.CHECKED,
  [MappingFilterType.DIRECT_ITEMS]: MappingFilterCheckState.CHECKED,
  [MappingFilterType.INHERITED_ITEMS]: MappingFilterCheckState.CHECKED,
};

const filterItemSorter = alphaSort<MappingFilterItem>(({ text }) => text);

export const projectMappingAttributeFiltersByIriSelector =
  (projectId: string, mappingId: string) => (state: ReduxState) =>
    state.ui.filtersByProjectMapping[projectId]?.[mappingId] || {};

export const projectMappingAttributeFilterSetsSelector = memoizeSelectorFactory(
  (projectId: string, mappingId: string) =>
    createSelector(
      projectMappingSelector(projectId, mappingId),
      projectMappingAttributeFiltersByIriSelector(projectId, mappingId),
      projectMappingAttributesSelector(projectId, mappingId),
      (mapping, filtersByIri = {}, attributes) => {
        const allAttributes = new Set<string>();
        const hiddenAttributes = new Set<string>();

        const directAttributes = new Set<string>();
        const hiddenDirectAttributes = new Set<string>();

        const inheritedAttributes = new Set<string>();
        const hiddenInheritedAttributes = new Set<string>();

        if (mapping?.to && attributes.length) {
          const classIri = getModelConceptClassValue(mapping.to);

          attributes.forEach(({ domains, value }) => {
            const isHidden = filtersByIri[value] === false;

            allAttributes.add(value);
            if (isHidden) {
              hiddenAttributes.add(value);
            }

            if (domains[classIri]) {
              directAttributes.add(value);
              if (isHidden) {
                hiddenDirectAttributes.add(value);
              }
            } else {
              inheritedAttributes.add(value);
              if (isHidden) {
                hiddenInheritedAttributes.add(value);
              }
            }
          });
        }

        return {
          allAttributes,
          hiddenAttributes,
          directAttributes,
          hiddenDirectAttributes,
          inheritedAttributes,
          hiddenInheritedAttributes,
        };
      }
    )
);

export const projectMappingAttributeFiltersSelector = memoizeSelectorFactory(
  (projectId: string, mappingId: string) =>
    createSelector(
      projectMappingAttributeFilterSetsSelector(projectId, mappingId),
      ({
        allAttributes,
        hiddenAttributes,
        directAttributes,
        hiddenDirectAttributes,
        inheritedAttributes,
        hiddenInheritedAttributes,
      }) => {
        if (!allAttributes.size || !hiddenAttributes.size) {
          return ALL_ATTRIBUTES_CHECKED_STATE;
        }

        let allAttributesValue = MappingFilterCheckState.CHECKED;
        let directAttributesValue = MappingFilterCheckState.CHECKED;
        let inheritedAttributesValue = MappingFilterCheckState.CHECKED;
        if (hiddenAttributes.size) {
          allAttributesValue =
            allAttributes.size === hiddenAttributes.size
              ? MappingFilterCheckState.UNCHECKED
              : MappingFilterCheckState.INDETERMINATE;
        }
        if (hiddenDirectAttributes.size) {
          directAttributesValue =
            directAttributes.size === hiddenDirectAttributes.size
              ? MappingFilterCheckState.UNCHECKED
              : MappingFilterCheckState.INDETERMINATE;
        }
        if (hiddenInheritedAttributes.size) {
          inheritedAttributesValue =
            inheritedAttributes.size === hiddenInheritedAttributes.size
              ? MappingFilterCheckState.UNCHECKED
              : MappingFilterCheckState.INDETERMINATE;
        }

        return {
          [MappingFilterType.ALL_ITEMS]: allAttributesValue,
          [MappingFilterType.DIRECT_ITEMS]: directAttributesValue,
          [MappingFilterType.INHERITED_ITEMS]: inheritedAttributesValue,
        };
      }
    )
);

export const projectMappingAttributeFilterItemsSelector =
  memoizeSelectorFactory((projectId: string, mappingId: string) =>
    createSelector(
      projectMappingAttributeFilterSetsSelector(projectId, mappingId),
      projectMappingAttributeFiltersSelector(projectId, mappingId),
      projectLabelsByIriSelector(projectId),
      (
        { hiddenAttributes, directAttributes, inheritedAttributes },
        filterState,
        labelsByIri
      ) => {
        const directAttributeSummaryItem: MappingFilterItem = {
          text: copy.components.projectMapping.filter.directAttributes,
          value: MappingFilterType.DIRECT_ITEMS,
          checkState: filterState.DIRECT_ITEMS,
        };
        const directAttributeItems: MappingFilterItem[] = Array.from(
          directAttributes
        )
          .map((value) => ({
            value,
            text: getLabelForIri(labelsByIri, value),
            checkState: hiddenAttributes.has(value)
              ? MappingFilterCheckState.UNCHECKED
              : MappingFilterCheckState.CHECKED,
          }))
          .sort(filterItemSorter);

        const inheritedAttributeSummaryItem: MappingFilterItem = {
          text: copy.components.projectMapping.filter.inheritedAttributes,
          value: MappingFilterType.INHERITED_ITEMS,
          checkState: filterState.INHERITED_ITEMS,
        };
        const inheritedAttributeItems: MappingFilterItem[] = Array.from(
          inheritedAttributes
        )
          .map((value) => ({
            value,
            text: getLabelForIri(labelsByIri, value),
            checkState: hiddenAttributes.has(value)
              ? MappingFilterCheckState.UNCHECKED
              : MappingFilterCheckState.CHECKED,
          }))
          .sort(filterItemSorter);

        const allAttributeItems = [
          directAttributeSummaryItem,
          ...directAttributeItems,
          inheritedAttributeSummaryItem,
          ...inheritedAttributeItems,
        ];

        return {
          allAttributeItems,
          directAttributeItems,
          inheritedAttributeItems,
        };
      }
    )
  );
